import React, { useState } from 'react';
import { ChevronLeft, User, Settings, RefreshCw, LogOut, Trash2, Key, ChevronRight, Bug } from 'lucide-react';
import { RebuildProgramSheet } from '../components/RebuildProgramSheet';
import { DeleteAccountModal } from '../components/DeleteAccountModal';
interface ProfileSettingsProps {
  onBack: () => void;
  onRebuildProgram: () => void;
  onTriggerError: () => void;
  onLogout: () => void;
  onDeleteAccount?: () => void;
}
export function ProfileSettings({
  onBack,
  onRebuildProgram,
  onTriggerError,
  onLogout,
  onDeleteAccount
}: ProfileSettingsProps) {
  const [showRebuildSheet, setShowRebuildSheet] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteAuthMethod, setDeleteAuthMethod] = useState<'email' | 'google'>('email');
  const [deleteInitialStep, setDeleteInitialStep] = useState<string>('confirm');
  const [showDebug, setShowDebug] = useState(false);
  const openDeleteFlow = (authMethod: 'email' | 'google' = 'email', step: string = 'confirm') => {
    setDeleteAuthMethod(authMethod);
    setDeleteInitialStep(step);
    setShowDeleteModal(true);
  };
  return <div className="flex flex-col h-full w-full bg-stone-50 overflow-hidden relative" data-id="element-890">
      {/* Debug Toggle */}
      <button onClick={() => setShowDebug(!showDebug)} className="absolute top-8 right-2 z-40 w-7 h-7 rounded-full bg-slate-800/70 text-white flex items-center justify-center hover:bg-slate-800 transition-colors" title="Debug panel" data-id="element-891">
        <Bug size={14} data-id="element-892" />
      </button>

      {/* Debug Panel */}
      {showDebug && <div className="absolute top-16 right-2 z-40 bg-slate-800 text-white rounded-2xl p-3 shadow-xl w-64 text-xs animate-in fade-in zoom-in-95 duration-150 max-h-[500px] overflow-y-auto" data-id="element-893">
          <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-894">
            Debug Panel
          </p>

          <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2 mt-1" data-id="element-895">
            Delete Account Flow
          </p>
          <button onClick={() => {
        setShowDebug(false);
        openDeleteFlow('email', 'confirm');
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-896">
            ▶ Confirmation screen
          </button>
          <button onClick={() => {
        setShowDebug(false);
        openDeleteFlow('email', 'reauth-password');
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-897">
            ▶ Re-auth — password
          </button>
          <button onClick={() => {
        setShowDebug(false);
        openDeleteFlow('google', 'reauth-google');
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-898">
            ▶ Re-auth — Google
          </button>
          <button onClick={() => {
        setShowDebug(false);
        openDeleteFlow('email', 'reauth-error');
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-899">
            ▶ Re-auth failure
          </button>
          <button onClick={() => {
        setShowDebug(false);
        openDeleteFlow('email', 'delete-error');
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-900">
            ▶ Deletion failure
          </button>
          <button onClick={() => {
        setShowDebug(false);
        openDeleteFlow('email', 'success');
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-901">
            ▶ Deletion success
          </button>
          <button onClick={() => {
        setShowDebug(false);
        onDeleteAccount?.();
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium" data-id="element-902">
            ▶ Route to Welcome after deletion
          </button>
        </div>}

      {/* Header */}
      <div className="flex items-center justify-between p-6 pt-8 bg-stone-50/90 backdrop-blur-md z-20 shrink-0" data-id="element-903">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-slate-200/50 text-slate-600 transition-colors" aria-label="Go back" data-id="element-904">
          <ChevronLeft size={28} data-id="element-905" />
        </button>
        <h1 className="text-xl font-extrabold text-slate-800" data-id="element-906">
          Profile & Settings
        </h1>
        <div className="w-10" data-id="element-907" /> {/* Spacer */}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-12 space-y-6" data-id="element-908">
        {/* User Info */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100 flex items-center gap-4" data-id="element-909">
          <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center shrink-0" data-id="element-910">
            <User size={32} data-id="element-911" />
          </div>
          <div data-id="element-912">
            <h2 className="text-xl font-extrabold text-slate-800" data-id="element-913">
              Flex Muscleman
            </h2>
            <p className="text-sm font-medium text-slate-500" data-id="element-914">
              flex@gymbuddy.app
            </p>
          </div>
        </div>

        {/* Preferences */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100" data-id="element-915">
          <div className="flex items-center gap-2 text-slate-800 mb-4" data-id="element-916">
            <Settings size={20} className="text-slate-400" data-id="element-917" />
            <h3 className="font-extrabold" data-id="element-918">Training Preferences</h3>
          </div>

          <div className="space-y-3" data-id="element-919">
            <div className="flex justify-between items-center py-2 border-b border-slate-50" data-id="element-920">
              <span className="text-sm font-medium text-slate-500" data-id="element-921">
                Primary Goal
              </span>
              <span className="text-sm font-bold text-slate-800" data-id="element-922">
                Build Muscle
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-50" data-id="element-923">
              <span className="text-sm font-medium text-slate-500" data-id="element-924">
                Frequency
              </span>
              <span className="text-sm font-bold text-slate-800" data-id="element-925">
                3 days / week
              </span>
            </div>
            <div className="flex justify-between items-center py-2" data-id="element-926">
              <span className="text-sm font-medium text-slate-500" data-id="element-927">
                Structure
              </span>
              <span className="text-sm font-bold text-slate-800" data-id="element-928">
                Full Body
              </span>
            </div>
          </div>
        </div>

        {/* Program Actions */}
        <div className="bg-white rounded-3xl p-2 shadow-sm border border-slate-100" data-id="element-929">
          <button onClick={() => setShowRebuildSheet(true)} className="w-full flex items-center justify-between p-3 rounded-2xl hover:bg-slate-50 transition-colors text-left" data-id="element-930">
            <div className="flex items-center gap-3" data-id="element-931">
              <div className="w-10 h-10 bg-blue-50 text-blue-500 rounded-xl flex items-center justify-center" data-id="element-932">
                <RefreshCw size={20} data-id="element-933" />
              </div>
              <div data-id="element-934">
                <span className="font-bold text-slate-800 block" data-id="element-935">
                  Rebuild Current Program
                </span>
                <span className="text-xs font-medium text-slate-500" data-id="element-936">
                  Generate a fresh plan
                </span>
              </div>
            </div>
            <ChevronRight size={20} className="text-slate-400" data-id="element-937" />
          </button>
        </div>

        {/* Account Actions */}
        <div className="bg-white rounded-3xl p-2 shadow-sm border border-slate-100" data-id="element-938">
          <button onClick={onTriggerError} className="w-full flex items-center justify-between p-3 rounded-2xl hover:bg-slate-50 transition-colors text-left" data-id="element-939">
            <div className="flex items-center gap-3" data-id="element-940">
              <div className="w-10 h-10 bg-slate-50 text-slate-500 rounded-xl flex items-center justify-center" data-id="element-941">
                <Key size={20} data-id="element-942" />
              </div>
              <span className="font-bold text-slate-800" data-id="element-943">Change Password</span>
            </div>
            <ChevronRight size={20} className="text-slate-400" data-id="element-944" />
          </button>

          <div className="h-px bg-slate-100 mx-3 my-1" data-id="element-945" />

          <button onClick={onLogout} className="w-full flex items-center justify-between p-3 rounded-2xl hover:bg-slate-50 transition-colors text-left" data-id="element-946">
            <div className="flex items-center gap-3" data-id="element-947">
              <div className="w-10 h-10 bg-slate-50 text-slate-500 rounded-xl flex items-center justify-center" data-id="element-948">
                <LogOut size={20} data-id="element-949" />
              </div>
              <span className="font-bold text-slate-800" data-id="element-950">Log Out</span>
            </div>
          </button>

          <div className="h-px bg-slate-100 mx-3 my-1" data-id="element-951" />

          <button onClick={() => openDeleteFlow('email', 'confirm')} className="w-full flex items-center justify-between p-3 rounded-2xl hover:bg-pink-50 transition-colors text-left group" data-id="element-952">
            <div className="flex items-center gap-3" data-id="element-953">
              <div className="w-10 h-10 bg-pink-50 text-pink-500 rounded-xl flex items-center justify-center group-hover:bg-pink-100 transition-colors" data-id="element-954">
                <Trash2 size={20} data-id="element-955" />
              </div>
              <span className="font-bold text-pink-500" data-id="element-956">Delete Account</span>
            </div>
          </button>
        </div>
      </div>

      {/* Modals */}
      {showRebuildSheet && <RebuildProgramSheet onNext={() => {
      setShowRebuildSheet(false);
      onRebuildProgram();
    }} onClose={() => setShowRebuildSheet(false)} data-id="element-957" />}

      {showDeleteModal && <DeleteAccountModal onClose={() => setShowDeleteModal(false)} onDeleted={() => {
      setShowDeleteModal(false);
      onDeleteAccount?.();
    }} authMethod={deleteAuthMethod} initialStep={deleteInitialStep as any} data-id="element-958" />}
    </div>;
}